'use client';

import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import { Bug } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@kit/ui/utils';

import { shouldPreventDismissableLayerOutside } from '~/home/_components/setup-assistant/use-dismiss-shield';

import { useBugReport } from './bug-report-context';

const STORAGE_KEY = 'askai.bug-report-button.position';
const BUTTON_SIZE = 48; // must match the className sizing below
const VIEWPORT_PADDING = 12;
const DRAG_THRESHOLD_PX = 4;

interface Position {
  x: number;
  y: number;
}

// useIsomorphicLayoutEffect to avoid SSR warnings — we only touch window on the client.
const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const subscribeToHydration = () => () => {};
const getClientHydrationSnapshot = () => true;
const getServerHydrationSnapshot = () => false;

// Access control happens server-side: the admin layout only renders for team
// members, and the (tenant) mount (TenantBugReport) checks membership before
// mounting the provider + button. This component just renders.
export function FloatingBugReportButton() {
  const hydrated = useSyncExternalStore(
    subscribeToHydration,
    getClientHydrationSnapshot,
    getServerHydrationSnapshot,
  );

  // A modal can add aria-hidden to the streamed launcher before its Suspense
  // boundary hydrates. Mount it afterward so React owns the initial DOM.
  return hydrated ? <FloatingBugReportLauncher /> : null;
}

function FloatingBugReportLauncher() {
  const { t } = useTranslation('common');
  const { open: openBugReport } = useBugReport();

  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const dragState = useRef<{
    pointerId: number;
    startPointerX: number;
    startPointerY: number;
    startButtonX: number;
    startButtonY: number;
    moved: boolean;
  } | null>(null);

  const [position, setPosition] = useState<Position | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Restore saved position on mount; clamp if viewport shrank since it was saved.
  useIsomorphicLayoutEffect(() => {
    const saved = window.localStorage.getItem(STORAGE_KEY);

    if (saved) {
      try {
        const parsed = JSON.parse(saved) as Position;

        if (
          typeof parsed.x === 'number' &&
          typeof parsed.y === 'number' &&
          Number.isFinite(parsed.x) &&
          Number.isFinite(parsed.y)
        ) {
          setPosition(clampToViewport(parsed));
        }
      } catch {
        // corrupt value — ignore and fall back to the default CSS position
      }
    }
  }, []);

  // Keep the button on screen across window resizes.
  useEffect(() => {
    const handleResize = () => {
      setPosition((current) => (current ? clampToViewport(current) : null));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const preventDismiss = (event: Event) => {
      const isOnButton = (target: EventTarget | null) =>
        target instanceof Node && buttonRef.current?.contains(target) === true;

      if (shouldPreventDismissableLayerOutside(event, isOnButton)) {
        event.preventDefault();
      }
    };

    document.addEventListener(
      'dismissableLayer.pointerDownOutside',
      preventDismiss,
      true,
    );
    document.addEventListener(
      'dismissableLayer.focusOutside',
      preventDismiss,
      true,
    );

    return () => {
      document.removeEventListener(
        'dismissableLayer.pointerDownOutside',
        preventDismiss,
        true,
      );
      document.removeEventListener(
        'dismissableLayer.focusOutside',
        preventDismiss,
        true,
      );
    };
  }, []);

  const onPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      if (event.button !== 0) return; // left click / primary touch only
      if (!buttonRef.current) return;

      const rect = buttonRef.current.getBoundingClientRect();

      dragState.current = {
        pointerId: event.pointerId,
        startPointerX: event.clientX,
        startPointerY: event.clientY,
        startButtonX: rect.left,
        startButtonY: rect.top,
        moved: false,
      };

      // Capture so we keep receiving move/up events even if the pointer leaves the button.
      buttonRef.current.setPointerCapture(event.pointerId);
    },
    [],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      const state = dragState.current;
      if (!state || state.pointerId !== event.pointerId) return;

      const dx = event.clientX - state.startPointerX;
      const dy = event.clientY - state.startPointerY;

      if (!state.moved && Math.hypot(dx, dy) > DRAG_THRESHOLD_PX) {
        state.moved = true;
        setIsDragging(true);
      }

      if (state.moved) {
        setPosition(
          clampToViewport({
            x: state.startButtonX + dx,
            y: state.startButtonY + dy,
          }),
        );
      }
    },
    [],
  );

  const onPointerUp = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      const state = dragState.current;
      if (!state || state.pointerId !== event.pointerId) return;

      const wasDrag = state.moved;
      dragState.current = null;
      setIsDragging(false);

      if (buttonRef.current?.hasPointerCapture(event.pointerId)) {
        buttonRef.current.releasePointerCapture(event.pointerId);
      }

      if (wasDrag) {
        // Persist the final resting position.
        setPosition((current) => {
          if (current) {
            try {
              window.localStorage.setItem(STORAGE_KEY, JSON.stringify(current));
            } catch {
              // localStorage unavailable — not fatal, position just won't persist
            }
          }
          return current;
        });
      } else {
        // Treated as a click.
        openBugReport();
      }
    },
    [openBugReport],
  );

  const usingSavedPosition = position !== null;

  return (
    <button
      ref={buttonRef}
      type="button"
      aria-label={t('bugReport.floatingButtonLabel', {
        defaultValue: 'Report a bug',
      })}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openBugReport();
        }
      }}
      className={cn(
        // z-[9000] keeps the launcher on top of ALL app overlays — standard
        // dialogs/drawers/sheets (z-50), the native admin sidebar (z-[60]), and
        // the elevated popovers that some pages use (z-[100]/z-[200], e.g. the
        // resources time-slot cards) — so the bug tool is always reachable, even
        // with a popup open. It intentionally stays BELOW the setup assistant /
        // guided-tour layer (z-[10050]+) so a running tour still covers it.
        // pointer-events-auto keeps it clickable even when an open modal layer
        // locks `body { pointer-events: none }` (the dismiss shield above then
        // stops that click from closing the open component).
        'pointer-events-auto fixed z-[9000] flex h-12 w-12 items-center justify-center',
        'bg-primary text-primary-foreground rounded-full shadow-lg',
        'touch-none transition-shadow select-none',
        'focus-visible:ring-ring hover:shadow-xl focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none',
        isDragging ? 'cursor-grabbing shadow-2xl' : 'cursor-grab',
        'print:hidden',
        // Default resting place: bottom-RIGHT. It used to default to bottom-4
        // left-4, which on desktop lands directly on top of the sidebar footer
        // — the account switcher and avatar sat underneath it until the user
        // discovered the button could be dragged. The right edge is empty in
        // both LTR and RTL (the sidebar flips sides, this stays clear of it).
        !usingSavedPosition && 'right-4 bottom-4',
      )}
      style={
        usingSavedPosition
          ? { left: `${position.x}px`, top: `${position.y}px` }
          : undefined
      }
      data-test="floating-bug-report-button"
    >
      <Bug className="h-5 w-5" aria-hidden="true" />
    </button>
  );
}

function clampToViewport(p: Position): Position {
  const maxX = window.innerWidth - BUTTON_SIZE - VIEWPORT_PADDING;
  const maxY = window.innerHeight - BUTTON_SIZE - VIEWPORT_PADDING;

  return {
    x: Math.max(VIEWPORT_PADDING, Math.min(maxX, p.x)),
    y: Math.max(VIEWPORT_PADDING, Math.min(maxY, p.y)),
  };
}
